import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PagerProps {
  total: number;
  page: number;          // 1-indexed
  pageSize: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

// Builds the visible page-number sequence. Always shows first and last
// page; collapses long runs in the middle with ellipses. Example:
//   1 … 4 5 [6] 7 8 … 42
function buildPageList(page: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const result: Array<number | 'ellipsis'> = [1];
  const start = Math.max(2, page - 2);
  const end = Math.min(totalPages - 1, page + 2);
  if (start > 2) result.push('ellipsis');
  for (let i = start; i <= end; i++) result.push(i);
  if (end < totalPages - 1) result.push('ellipsis');
  result.push(totalPages);
  return result;
}

export function Pager({
  total,
  page,
  pageSize,
  onChange,
  onPageSizeChange,
  pageSizeOptions = [50, 100, 200],
  className,
}: PagerProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const firstShown = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastShown = Math.min(currentPage * pageSize, total);

  const pages = buildPageList(currentPage, totalPages);

  const baseBtn =
    'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 text-sm rounded-md border transition-colors';
  const idleBtn = `${baseBtn} border-gray-200 text-gray-600 hover:bg-gray-50`;
  const activeBtn = `${baseBtn} border-violet-600 bg-violet-600 text-white`;
  const disabledBtn = `${baseBtn} border-gray-100 text-gray-300 cursor-not-allowed`;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl ${className ?? ''}`}
    >
      <div className="text-xs text-gray-500">
        {total === 0 ? (
          'No results'
        ) : (
          <>
            Showing <span className="font-medium text-gray-700">{firstShown}</span>–
            <span className="font-medium text-gray-700">{lastShown}</span> of{' '}
            <span className="font-medium text-gray-700">{total}</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className={currentPage <= 1 ? disabledBtn : idleBtn}
          title="Previous page"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>

        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <span key={`e-${i}`} className="px-1 text-gray-400 text-xs select-none">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={p === currentPage ? activeBtn : idleBtn}
            >
              {p}
            </button>
          ),
        )}

        <button
          onClick={() => onChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={currentPage >= totalPages ? disabledBtn : idleBtn}
          title="Next page"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {onPageSizeChange && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            className="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
